import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  EmptyVendors,
  EmptyFilteredTransactions,
} from "@/components/ui/empty-state-presets";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Check,
  X,
  TrendingUp,
  Package,
  AlertCircle,
  Shield,
  Sparkles,
  Link2,
  Users,
  RefreshCw,
  Brain,
  Loader2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { getLoginUrl } from "@/const";
import { VendorLinkDialog } from "@/components/VendorLinkDialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

export default function Vendors() {
  return (
    <DashboardLayout>
      <VendorsContent />
    </DashboardLayout>
  );
}

function VendorsContent() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { selectedCompanyId } = useCompany();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUIDLookupModal, setShowUIDLookupModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [uidInput, setUidInput] = useState("");
  const [lookupResult, setLookupResult] = useState<any>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Duplicate detection state
  const [similarVendors, setSimilarVendors] = useState<
    Array<{
      id: number;
      name: string;
      similarity: number;
      matchType: "exact" | "fuzzy" | "uid";
      uid?: string;
      category?: string;
    }>
  >([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkTarget, setLinkTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Re-analysis state
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalyzingVendorId, setReanalyzingVendorId] = useState<number | null>(
    null
  );
  const [reanalysisProgress, setReanalysisProgress] = useState<{
    processed: number;
    total: number;
    improved: number;
  } | null>(null);

  // Utils for imperative queries
  const utils = trpc.useUtils();

  // Queries
  const {
    data: vendorsData,
    isLoading,
    refetch,
  } = trpc.vendorIntelligence.listVendors.useQuery(
    {
      companyId: selectedCompanyId!,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    },
    { enabled: !!selectedCompanyId }
  );
  const vendors = vendorsData?.items;
  const vendorTotal = vendorsData?.total ?? 0;
  const totalPages = Math.ceil(vendorTotal / pageSize);

  const { data: stats } = trpc.vendorIntelligence.getVendorStats.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  // Mutations
  // Note: Vendor deletion is done via mergeVendors (archive) instead of hard delete

  // Create vendor mutation
  const createVendorMutation = trpc.vendorIntelligence.upsertVendor.useMutation(
    {
      onSuccess: () => {
        toast.created("Vendor", {});
        refetch();
        setShowUIDLookupModal(false);
        setLookupResult(null);
        setUidInput("");
        setSimilarVendors([]);
      },
      onError: error => {
        toast.error(`Failed to create vendor: ${error.message}`, {
          onRetry: () => {
            if (lookupResult) {
              createVendorMutation.mutate({
                companyId: selectedCompanyId!,
                name: lookupResult.name,
                uid: lookupResult.uid,
              });
            }
          },
        });
      },
    }
  );

  // Link vendor mutation
  const linkVendorMutation = trpc.vendorIntelligence.linkVendors.useMutation({
    onSuccess: () => {
      toast.success("Vendors linked successfully");
      refetch();
      setShowLinkDialog(false);
      setLinkTarget(null);
      setSimilarVendors([]);
    },
    onError: error => {
      toast.error(`Failed to link vendors: ${error.message}`);
    },
  });

  const enrichVendorMutation = trpc.llmVendor.enrichVendor.useMutation({
    onSuccess: data => {
      if (data.found && data.companyInfo) {
        toast.success(
          `Found company: ${data.companyInfo.name} (${data.confidence}% confidence)`
        );
        // Pre-fill form with enriched data
        if (selectedVendor) {
          setSelectedVendor({
            ...selectedVendor,
            name: data.companyInfo.name,
            uid: data.companyInfo.uid,
            mwstRegistered: data.companyInfo.mwstRegistered,
            mwstNumber: data.companyInfo.mwstNumber,
          });
        }
      } else {
        toast.warning("Company not found in Swiss Commercial Register");
      }
    },
    onError: error => {
      toast.error(`Lookup failed: ${error.message}`);
    },
  });

  // Batch re-analyze all vendors mutation
  const batchReanalyzeMutation =
    trpc.vendorIntelligence.batchReanalyzeVendors.useMutation({
      onSuccess: data => {
        setReanalysisProgress({
          processed: data.processed,
          total: data.processed,
          improved: data.updated,
        });
        if (data.updated > 0) {
          toast.success(
            `Re-analysis complete! Improved ${data.updated} of ${data.processed} vendors.`
          );
        } else {
          toast.info(
            `Re-analysis complete. No improvements found for ${data.processed} vendors.`
          );
        }
        refetch();
        setIsReanalyzing(false);
      },
      onError: error => {
        toast.error(`Re-analysis failed: ${error.message}`);
        setIsReanalyzing(false);
        setReanalysisProgress(null);
      },
    });

  // Re-analyze single vendor mutation
  const reanalyzeVendorMutation =
    trpc.vendorIntelligence.reanalyzeVendor.useMutation({
      onSuccess: data => {
        setReanalyzingVendorId(null);
        if (data.changes.length > 0) {
          toast.success(
            `Vendor updated: ${data.vendor.name} (${data.changes.length} changes)`
          );
          refetch();
        } else {
          toast.info(`No improvements found for ${data.vendor.name}`);
        }
      },
      onError: error => {
        setReanalyzingVendorId(null);
        toast.error(`Re-analysis failed: ${error.message}`);
      },
    });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              Please sign in to access vendor management
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href={getLoginUrl()}>Sign In</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!selectedCompanyId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>No Company Selected</CardTitle>
            <CardDescription>
              Please select a company to manage vendors
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Note: Vendor deletion is done via mergeVendors (archive) instead of hard delete
  // const handleDeleteVendor = (vendorId: number) => {
  //   Archive vendor by merging into another vendor
  // };

  const handleEnrichVendor = (vendorName: string) => {
    enrichVendorMutation.mutate({ vendorName });
  };

  // Handle batch re-analysis of all vendors
  const handleBatchReanalyze = () => {
    if (!selectedCompanyId) return;
    setIsReanalyzing(true);
    setReanalysisProgress(null);
    batchReanalyzeMutation.mutate({
      companyId: selectedCompanyId,
      confidenceThreshold: 70,
    });
  };

  // Handle single vendor re-analysis
  const handleReanalyzeVendor = (vendorId: number) => {
    if (!selectedCompanyId) return;
    setReanalyzingVendorId(vendorId);
    reanalyzeVendorMutation.mutate({
      companyId: selectedCompanyId,
      vendorId,
    });
  };

  // UID lookup handler using trpc client
  const handleUIDLookup = async () => {
    if (!uidInput.trim()) {
      toast.warning("Please enter a Swiss UID");
      return;
    }

    setIsLookingUp(true);
    setLookupResult(null);
    setSimilarVendors([]);

    try {
      // Use tRPC utils to call the lookup query
      const result = await utils.llmVendor.lookupCompanyByUID.fetch({
        uid: uidInput,
      });
      setLookupResult(result);
      toast.success(`Found company: ${result.name}`);

      // Check for similar existing vendors
      if (selectedCompanyId && result.name) {
        try {
          const similar = await utils.vendorIntelligence.checkSimilar.fetch({
            companyId: selectedCompanyId,
            name: result.name,
            uid: result.uid,
          });

          if (similar.length > 0) {
            // Transform to match expected state type
            const transformed = similar.map(match => ({
              id: match.vendor.id,
              name: match.vendor.name,
              similarity: match.confidence,
              matchType:
                match.matchType === "exact_uid"
                  ? ("uid" as const)
                  : ("fuzzy" as const),
              uid: match.vendor.uid ?? undefined,
              category: match.vendor.category ?? undefined,
            }));
            setSimilarVendors(transformed);
            toast.warning(
              `Found ${similar.length} similar vendor(s) - please review before adding`
            );
          }
        } catch {
          // Ignore duplicate check errors - not critical
        }
      }
    } catch (error: unknown) {
      const err = error as { message?: string; data?: { code?: string } };
      if (
        err.message?.includes("NOT_FOUND") ||
        err.data?.code === "NOT_FOUND"
      ) {
        toast.warning("Company not found in Swiss Commercial Register");
      } else {
        toast.error(`Lookup failed: ${err.message || "Unknown error"}`);
      }
      setLookupResult(null);
    } finally {
      setIsLookingUp(false);
    }
  };

  // Create vendor from lookup result
  const handleCreateVendorFromLookup = () => {
    if (!lookupResult || !selectedCompanyId) return;

    createVendorMutation.mutate({
      companyId: selectedCompanyId,
      name: lookupResult.name,
      uid: lookupResult.uid,
      swissVendor: true,
      mwstRegistered: lookupResult.mwstRegistered || false,
      mwstNumber: lookupResult.mwstNumber,
      defaultMwstRate: "7.7",
      category: "Supplier",
      recognitionConfidence: 95,
    });
  };

  const filteredVendors = vendors?.filter(vendor => {
    const matchesSearch =
      vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.uid?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || vendor.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(
    new Set(vendors?.map(v => v.category).filter((c): c is string => !!c) || [])
  );

  const getMWSTBadge = (vendor: any) => {
    if (!vendor.mwstRegistered) {
      return (
        <Badge variant="secondary">
          <X className="h-3 w-3 mr-1" />
          Not Registered
        </Badge>
      );
    }

    const rate = vendor.defaultMwstRate;
    const colors: Record<string, string> = {
      "0": "bg-muted",
      "2.5": "bg-info",
      "3.7": "bg-info",
      "7.7": "bg-primary",
    };

    return (
      <Badge
        className={`${colors[rate || "7.7"]} hover:${colors[rate || "7.7"]}`}
      >
        <Check className="h-3 w-3 mr-1" />
        {rate || "7.7"}% MWST
      </Badge>
    );
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 80) {
      return (
        <Badge className="bg-success hover:bg-success/80">{confidence}%</Badge>
      );
    } else if (confidence >= 60) {
      return (
        <Badge className="bg-warning hover:bg-warning/80">{confidence}%</Badge>
      );
    } else {
      return <Badge variant="secondary">{confidence}%</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Vendor Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage your business vendors and suppliers
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleBatchReanalyze}
            disabled={isReanalyzing || !vendors || vendors.length === 0}
          >
            {isReanalyzing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Re-analyzing...
              </>
            ) : (
              <>
                <Brain className="mr-2 h-4 w-4" />
                Re-analyze All
              </>
            )}
          </Button>
          <Button onClick={() => setShowUIDLookupModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Vendor
          </Button>
        </div>
      </div>

      {/* Re-analysis Progress */}
      {reanalysisProgress && (
        <Alert className="border-[var(--info)] bg-info/5">
          <Brain className="h-4 w-4" />
          <AlertTitle>Re-analysis Results</AlertTitle>
          <AlertDescription>
            Processed {reanalysisProgress.processed} vendors, improved{" "}
            {reanalysisProgress.improved}.
            {reanalysisProgress.improved > 0 && (
              <span className="ml-2 text-success">
                {Math.round(
                  (reanalysisProgress.improved / reanalysisProgress.processed) *
                    100
                )}
                % improvement rate
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Vendors</p>
                  <p className="text-3xl font-bold">{stats.totalVendors}</p>
                </div>
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Swiss Vendors</p>
                  <p className="text-3xl font-bold text-destructive">
                    {stats.swissVendors}
                  </p>
                </div>
                <Shield className="h-8 w-8 text-destructive" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    MWST Registered
                  </p>
                  <p className="text-3xl font-bold text-success">
                    {stats.mwstRegistered}
                  </p>
                </div>
                <Check className="h-8 w-8 text-success" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="text-3xl font-bold">
                    CHF {((stats.totalSpent || 0) / 100).toFixed(0)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Vendors</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by name or UID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-64">
              <Label htmlFor="category">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Vendors ({filteredVendors?.length || 0})</CardTitle>
          <CardDescription>
            Manage your vendor database with MWST compliance tracking
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Skeleton className="h-4 w-16 animate-shimmer" />
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-20 animate-shimmer" />
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-24 animate-shimmer" />
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-24 animate-shimmer" />
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-20 animate-shimmer" />
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-24 animate-shimmer" />
                    </TableHead>
                    <TableHead className="text-right">
                      <Skeleton className="h-4 w-16 ml-auto animate-shimmer" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-4 w-4 animate-shimmer" />
                            <Skeleton className="h-4 w-32 animate-shimmer" />
                          </div>
                          <Skeleton className="h-3 w-24 animate-shimmer" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-20 rounded-full animate-shimmer" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-24 rounded-full animate-shimmer" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-8 animate-shimmer" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24 animate-shimmer" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-2 w-16 rounded-full animate-shimmer" />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Skeleton className="h-8 w-8 animate-shimmer" />
                          <Skeleton className="h-8 w-8 animate-shimmer" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : filteredVendors && filteredVendors.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>MWST Status</TableHead>
                    <TableHead>Transactions</TableHead>
                    <TableHead>Total Spent</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVendors.map(vendor => (
                    <TableRow key={vendor.id}>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{vendor.name}</span>
                          </div>
                          {vendor.uid && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {vendor.uid}
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            {vendor.swissVendor && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-destructive/10"
                              >
                                🇨🇭 Swiss
                              </Badge>
                            )}
                            {vendor.verifiedBy && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-info/10"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                            {vendor.sourceDocumentIds &&
                              vendor.sourceDocumentIds.length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs"
                                  title={`Extracted from ${vendor.sourceDocumentIds.length} document(s)`}
                                >
                                  {vendor.sourceDocumentIds.length} doc
                                  {vendor.sourceDocumentIds.length > 1
                                    ? "s"
                                    : ""}
                                </Badge>
                              )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline">
                            {vendor.category || "Uncategorized"}
                          </Badge>
                          {vendor.subcategory && (
                            <span className="text-xs text-muted-foreground">
                              {vendor.subcategory}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getMWSTBadge(vendor)}
                          {vendor.mwstNumber && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {vendor.mwstNumber}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span>{vendor.transactionCount || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        CHF {((vendor.totalSpent || 0) / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {getConfidenceBadge(vendor.recognitionConfidence || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReanalyzeVendor(vendor.id)}
                            disabled={reanalyzingVendorId === vendor.id}
                            title="Re-analyze vendor with AI"
                          >
                            {reanalyzingVendorId === vendor.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEnrichVendor(vendor.name)}
                            title="Lookup in Swiss Commercial Register"
                          >
                            <Sparkles className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedVendor(vendor);
                              setShowEditModal(true);
                            }}
                            title="Edit vendor"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {/* Delete button removed - use merge/archive instead */}
                          {/* <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteVendor(vendor.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button> */}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : searchQuery || categoryFilter !== "all" ? (
            <EmptyFilteredTransactions
              onClearFilters={() => {
                setSearchQuery("");
                setCategoryFilter("all");
              }}
            />
          ) : (
            <EmptyVendors onPrimaryAction={() => setShowUIDLookupModal(true)} />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      className={
                        currentPage === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          isActive={currentPage === pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className="cursor-pointer"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setCurrentPage(p => Math.min(totalPages, p + 1))
                      }
                      className={
                        currentPage === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
              <p className="text-sm text-muted-foreground text-center mt-2">
                Showing {(currentPage - 1) * pageSize + 1}-
                {Math.min(currentPage * pageSize, vendorTotal)} of {vendorTotal}{" "}
                vendors
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* UID Lookup Modal */}
      <Dialog
        open={showUIDLookupModal}
        onOpenChange={open => {
          setShowUIDLookupModal(open);
          if (!open) {
            setLookupResult(null);
            setUidInput("");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Vendor with UID Lookup</DialogTitle>
            <DialogDescription>
              Enter a Swiss UID to automatically populate vendor information
              from the Commercial Register
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="uid">Swiss UID</Label>
              <div className="flex gap-2">
                <Input
                  id="uid"
                  placeholder="CHE-XXX.XXX.XXX"
                  value={uidInput}
                  onChange={e => setUidInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleUIDLookup();
                  }}
                />
                <Button
                  onClick={handleUIDLookup}
                  disabled={isLookingUp || !uidInput.trim()}
                >
                  {isLookingUp ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Format: CHE-123.456.789 or CHE123456789
              </p>
            </div>

            {/* Lookup Result */}
            {lookupResult && (
              <Card className="border-[var(--success)]">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-success mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <div>
                        <p className="font-semibold">{lookupResult.name}</p>
                        <p className="text-sm text-muted-foreground font-mono">
                          {lookupResult.uid}
                        </p>
                      </div>
                      {lookupResult.address && (
                        <p className="text-sm text-muted-foreground">
                          {lookupResult.address}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Badge variant="outline" className="bg-destructive/10">
                          Swiss Company
                        </Badge>
                        {lookupResult.mwstRegistered ? (
                          <Badge className="bg-success/10 text-success">
                            <Check className="h-3 w-3 mr-1" />
                            MWST Registered
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <X className="h-3 w-3 mr-1" />
                            Not MWST Registered
                          </Badge>
                        )}
                      </div>
                      {lookupResult.mwstNumber && (
                        <p className="text-xs text-muted-foreground">
                          MWST: {lookupResult.mwstNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Duplicate Warning */}
            {similarVendors.length > 0 && (
              <Alert className="border-[var(--warning)] bg-warning/5">
                <Users className="h-4 w-4" />
                <AlertTitle>Similar vendors found</AlertTitle>
                <AlertDescription>
                  <p className="mb-3">
                    The following existing vendors may be duplicates. Consider
                    linking instead of creating a new vendor.
                  </p>
                  <div className="space-y-2">
                    {similarVendors.map(match => (
                      <div
                        key={match.id}
                        className="flex items-center justify-between p-2 bg-background rounded border"
                      >
                        <div>
                          <span className="font-medium">{match.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {Math.round(match.similarity * 100)}% match
                          </span>
                          {match.matchType === "uid" && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Same UID
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setLinkTarget({ id: match.id, name: match.name });
                            setShowLinkDialog(true);
                          }}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          Link
                        </Button>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUIDLookupModal(false)}
            >
              Cancel
            </Button>
            {lookupResult && (
              <Button
                onClick={handleCreateVendorFromLookup}
                disabled={createVendorMutation.isPending}
              >
                {createVendorMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-background border-t-transparent" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {similarVendors.length > 0 ? "Create Anyway" : "Add Vendor"}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Link Dialog */}
      {linkTarget && lookupResult && (
        <VendorLinkDialog
          open={showLinkDialog}
          onOpenChange={setShowLinkDialog}
          vendorA={{ id: linkTarget.id, name: linkTarget.name }}
          vendorB={{ id: 0, name: lookupResult.name }} // New vendor (ID 0 = not yet created)
          onConfirm={(relationshipType, primaryId) => {
            // Since the new vendor doesn't exist yet, we need to create it first, then link
            // For now, just create and let the user link manually after
            createVendorMutation.mutate({
              companyId: selectedCompanyId!,
              name: lookupResult.name,
              uid: lookupResult.uid,
              swissVendor: true,
              mwstRegistered: lookupResult.mwstRegistered || false,
              mwstNumber: lookupResult.mwstNumber,
              defaultMwstRate: "7.7",
              category: "Supplier",
              recognitionConfidence: 95,
            });
            toast.info(
              `Vendor created. You can link it to "${linkTarget.name}" from the vendor list.`
            );
            setShowLinkDialog(false);
            setLinkTarget(null);
          }}
        />
      )}
    </div>
  );
}
